import React, { useEffect } from "react";
import ImageComponent from "@project-stories/elements/Image/Image";

declare global {
  interface Window {
    Brauz_open_book_a_stylist_dialog?: (params: any) => void;
    Brauz?: any;
  }
}
interface StoreDetailsProps {
  store: {
    id: number;
    name: string;
    address_street_1?: string;
    address_street_2?: string;
    city?: string;
    postcode?: string | number;
    state?: string;
    phone_number?: string;
    store_email?: string;
    distance: string;
    hours: string;
    services: string[];
    store_image?: string;
  };
  onBack: () => void;
  hasManualSearch: boolean;
}

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const formatTime = (time: string) => {
  if (!time) return "";
  let hours = Math.floor(parseInt(time) / 100);
  let minutes = parseInt(time) % 100;
  const suffix = hours >= 12 ? "PM" : "AM";
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes.toString().padStart(2, "0")} ${suffix}`;
};

const findHoursForDay = (day: string, storeHours: any[]) => {
  // day_start == day_end == day
  const exactMatch = storeHours.find(
    (hours) => hours.day_start === day && hours.day_end === day
  );

  if (exactMatch) {
    return `${formatTime(exactMatch.time_start)} - ${formatTime(exactMatch.time_end)}`;
  }

  // Check if within a day range
  for (const hours of storeHours) {
    const startIndex = daysOfWeek.indexOf(hours.day_start);
    const endIndex = daysOfWeek.indexOf(hours.day_end);
    const currentIndex = daysOfWeek.indexOf(day);

    if (startIndex <= currentIndex && currentIndex <= endIndex) {
      return `${formatTime(hours.time_start)} - ${formatTime(hours.time_end)}`;
    }
  }

  // No match found: assume Closed
  return "Closed";
};

const handleBookAppointment = (store) => {
  if (typeof window.Brauz_open_book_a_stylist_dialog === 'function') {
    window.Brauz_open_book_a_stylist_dialog({
      selected_store_id: `${store.store_number}`
    });

  } else {
    alert("Booking system is unavailable. Please try again later.");
  }
};

const StoreFinderDetails: React.FC<StoreDetailsProps> = ({ store, onBack, hasManualSearch }) => {
  useEffect(() => {
    console.log('hasManualSearch', hasManualSearch)
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);
  
  return (
    <div className="lg:pl-[21px]">
      <button 
        className="flex w-full justify-stretch items-center text-black text-base/[22px] font-normal normal-case tracking-normal px-7 lg:py-5 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-[95%] after:border-b after:border-[#A2A9AD]"
        onClick={onBack}
      >
        <i className="text-[#BBBBBD] icon text-2xl icon-chevron-left"></i>
        <span>Back to Store List</span>
      </button>

      <div className="w-full bg-[#ECEEEF] lg:overflow-y-auto lg:h-screen">
        <div className="relative">
          <ImageComponent
            classes="object-cover w-full h-full"
            alt={store.name}
            imageUrl={store.store_image || "https://cdn.shopify.com/s/files/1/0237/4546/2352/files/store-image-placeholder.jpg?v=1742266561"}
          />
          
          <h1 className="absolute text-white bottom-4 max-lg:px-5 lg:pl-8">{store.name}</h1>
        </div>

        <div className="px-4 py-6 lg:p-8">
          <div className="flex justify-between gap-2 mt-2">
            <p className="text-black font-normal b1 lg:max-w-[300px]">
              {store.address_street_1}<br />
              {store.address_street_2 && <>{store.address_street_2}<br /></>}
              {store.city} {store.state} {store.postcode}
            </p>

            <a className="hidden text-[#00263E] cursor-pointer hover:no-underline lg:py-2">
              <i className="mr-1 icon icon-location-wide"></i> Set as My Store
            </a>
          </div>

          <div className="text-[#00263E] text-base/4">
            {store.phone_number && (
              <p className="flex items-center gap-2 border-b border-[#BBBBBD] py-4 lg:gap-4">
                <i className="icon icon-phone"></i>
                {store.phone_number}
              </p>
            )}
            {store.store_email && (
              <p className="flex items-center gap-2 border-b border-[#BBBBBD] py-4 lg:gap-4">
                <i className="icon icon-mail"></i>
                {store.store_email}
              </p>
            )}
          </div>

          <div className="flex gap-4 mt-4 max-lg:flex-col lg:mt-8">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-[#00263E] py-4 gap-2 text-[#00263E] flex items-center justify-center p-0 font-medium text-base/4 tracking-[1.12px] uppercase hover:no-underline lg:max-w-[226px] lg:w-1/2"
            >
              <i className="mr-2 icon icon-directions"></i> Get Directions
            </a>
            <button
              className="px-4 flex-1 bg-white lg:w-1/2 border border-[#00263E] py-4 gap-2 text-[#00263E] flex items-center justify-center p-0 font-medium text-base/4 tracking-[1.12px]"
              onClick={() => handleBookAppointment(store)}
            >
              <i className="mr-2 icon icon-calendar"></i> Book an Appointment
            </button>
          </div>

          {hasManualSearch && store.distance && (
            <span className="text-[#54565A] mt-2 b3 block">
              {store.distance} away from you
            </span>
          )}
          
          {store.store_services?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-medium text-black uppercase bc border-b border-[#54565A] block py-2">
                At this store
              </h3>
              <ul className="p-0">
                {store.store_services?.map((service, index) => (
                  <li key={index} className="flex items-center gap-2 text-base text-black border-b border-[#BBBBBD] py-3">
                    <ImageComponent
                      classes="w-6 h-6 object-contain"
                      alt={service.service_label}
                      imageUrl={service.service_icon}
                    />
                    {service.service_label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8">
            <h3 className="py-2 text-black bc border-b border-[#54565A]">
              Opening Hours
            </h3>
            <ul className="p-0 list-none">
              {daysOfWeek.map((day, index) => (
                <li key={index} className="flex text-base text-black">
                  <span className="w-1/2 py-1">{day}</span>
                  <span className="w-1/2 py-1">
                    {store.data_times ? findHoursForDay(day, store.data_times) : "Closed"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreFinderDetails;